import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tab,
  Tabs,
  Badge,
} from "@mui/material";
import {
  Extension as ExtensionIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { Firestore } from "../../../../../components/FirebaseAuth/firebase";

const useStyles = makeStyles((theme) => ({
  workflowContainer: {
    padding: "16px",
    marginTop: "24px",
  },
  tabContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: "16px",
  },
  activityList: {
    maxHeight: "600px",
    overflow: "auto",
  },
  activityItem: {
    marginBottom: "8px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  statusIcon: {
    width: "32px",
    height: "32px",
  },
  timestamp: {
    fontSize: "12px",
    color: "#666",
  },
  actionButton: {
    minWidth: "auto",
    padding: "4px 8px",
  },
  emptyState: {
    textAlign: "center",
    padding: "48px 16px",
    color: "#666",
  },
}));

const getStatusIcon = (status, type, classes) => {
  const iconProps = { className: classes.statusIcon };
  
  switch (status) {
    case "success":
    case "completed":
    case "active":
      return <CheckCircleIcon {...iconProps} color="success" />;
    case "error":
    case "failed":
      return <ErrorIcon {...iconProps} color="error" />;
    case "warning":
    case "pending":
      return <WarningIcon {...iconProps} color="warning" />;
    case "running":
    case "in_progress":
      return <CircularProgress size={24} color="primary" />;
    default:
      return <InfoIcon {...iconProps} color="info" />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "success":
    case "completed":
    case "active":
      return "success";
    case "error":
    case "failed":
      return "error";
    case "warning":
    case "pending":
      return "warning";
    case "running":
    case "in_progress":
      return "info";
    default:
      return "default";
  }
};

export const SiteWorkflows = ({ siteData }) => {
  const classes = useStyles();
  const params = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [activities, setActivities] = useState([]);
  const [extensionLogs, setExtensionLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailDialog, setDetailDialog] = useState({ open: false, activity: null });

  const fetchActivities = useCallback(async () => {
    if (!params.accountId || !params.siteId) return;

    try {
      setLoading(true);
      
      // Fetch general site activities
      const activitiesRef = collection(
        Firestore,
        `accounts/${params.accountId}/sites/${params.siteId}/activities`
      );
      const activitiesQuery = query(
        activitiesRef,
        orderBy("timestamp", "desc"),
        limit(50)
      );
      
      const activitiesSnapshot = await getDocs(activitiesQuery);
      const activitiesData = activitiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: "activity"
      }));

      // Fetch extension logs
      const extensionLogsRef = collection(
        Firestore,
        `accounts/${params.accountId}/sites/${params.siteId}/extensionLogs`
      );
      const extensionLogsQuery = query(
        extensionLogsRef,
        orderBy("startTime", "desc"),
        limit(50)
      );
      
      const extensionLogsSnapshot = await getDocs(extensionLogsQuery);
      const extensionLogsData = extensionLogsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: "extension",
        timestamp: doc.data().startTime
      }));

      setActivities(activitiesData);
      setExtensionLogs(extensionLogsData);
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoading(false);
    }
  }, [params.accountId, params.siteId]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleViewDetails = (activity) => {
    setDetailDialog({ open: true, activity });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, "MMM dd, yyyy 'at' h:mm a");
  };

  const renderActivityItem = (activity) => {
    const isExtension = activity.type === "extension";
    const status = activity.status || "info";
    const title = isExtension 
      ? `Extension: ${activity.extensionId || 'Unknown'}`
      : activity.action || activity.title || "Site Activity";
    
    const getDescription = () => {
      if (isExtension) {
        return `${activity.triggeredBy || 'Auto'} execution - ${activity.functionExecutionTime || 0}ms`;
      }
      
      // Handle activity.details which might be an object
      if (activity.description && typeof activity.description === 'string') {
        return activity.description;
      }
      
      if (activity.details) {
        if (typeof activity.details === 'string') {
          return activity.details;
        }
        if (typeof activity.details === 'object') {
          // Convert object to readable string
          if (activity.details.extensionName) {
            return `Extension: ${activity.details.extensionName} - Status: ${activity.details.status || 'Unknown'}`;
          }
          return JSON.stringify(activity.details);
        }
      }
      
      return "";
    };
    
    const description = getDescription();

    return (
      <ListItem key={activity.id} className={classes.activityItem}>
        <ListItemIcon>
          {isExtension ? (
            <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
              <ExtensionIcon fontSize="small" />
            </Avatar>
          ) : (
            getStatusIcon(status, activity.type, classes)
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle2">{title}</Typography>
              <Chip
                label={status}
                size="small"
                color={getStatusColor(status)}
                variant="outlined"
              />
            </Box>
          }
          secondary={
            <Box>
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
              <Typography className={classes.timestamp}>
                {formatTimestamp(activity.timestamp)}
              </Typography>
            </Box>
          }
        />
        <ListItemSecondaryAction>
          <Tooltip title="View Details">
            <IconButton
              edge="end"
              size="small"
              onClick={() => handleViewDetails(activity)}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  const renderActivitiesTab = () => {
    const allActivities = [...activities, ...extensionLogs]
      .sort((a, b) => {
        const dateA = a.timestamp?.toDate?.() || new Date(a.timestamp);
        const dateB = b.timestamp?.toDate?.() || new Date(b.timestamp);
        return dateB - dateA;
      });

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (allActivities.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <TimelineIcon sx={{ fontSize: 48, mb: 2, color: "#ccc" }} />
          <Typography variant="h6" color="text.secondary">
            No activity recorded yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Site activities and extension executions will appear here
          </Typography>
        </Box>
      );
    }

    return (
      <List className={classes.activityList}>
        {allActivities.map(renderActivityItem)}
      </List>
    );
  };

  const renderExtensionsTab = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (extensionLogs.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <ExtensionIcon sx={{ fontSize: 48, mb: 2, color: "#ccc" }} />
          <Typography variant="h6" color="text.secondary">
            No extension activities
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Extension executions and installations will appear here
          </Typography>
        </Box>
      );
    }

    return (
      <List className={classes.activityList}>
        {extensionLogs.map(renderActivityItem)}
      </List>
    );
  };

  return (
    <Card className={classes.workflowContainer}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Site Activity & Workflows
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchActivities}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        className={classes.tabContainer}
      >
        <Tab 
          label={
            <Badge badgeContent={activities.length + extensionLogs.length} color="primary" max={99}>
              All Activity
            </Badge>
          } 
        />
        <Tab 
          label={
            <Badge badgeContent={extensionLogs.length} color="secondary" max={99}>
              Extensions
            </Badge>
          } 
        />
      </Tabs>

      {activeTab === 0 && renderActivitiesTab()}
      {activeTab === 1 && renderExtensionsTab()}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, activity: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Activity Details</DialogTitle>
        <DialogContent>
          {detailDialog.activity && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {detailDialog.activity.type === "extension" 
                  ? `Extension: ${detailDialog.activity.extensionId}`
                  : detailDialog.activity.action || "Site Activity"
                }
              </Typography>
              <pre style={{ fontSize: "12px", overflow: "auto" }}>
                {JSON.stringify(detailDialog.activity, null, 2)}
              </pre>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, activity: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};